import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/shared/Button';
import MultiTab from '../../components/shared/MultiTab';
import DockUndockManager from '../../components/DockUndockManager';
import EditableHeader from '../../components/shared/EditableHeader';
import usePowerBI from '../../hooks/usePowerBI';
import PowerBIEmbed from '../../components/PowerBIEmbed';
import useFileUpload from '../../hooks/useFileUpload';
import { fileService, powerBiService, reportsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import SidebarLayout from '../../components/SidebarLayout';
import AcademicsAgentPanel from '../../components/AcademicsAgentPanel';
import styles from './DashboardPage.module.css';

// import ReportViewer from '../../components/RLSPowerBI/ReportViewer';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useToast();
  const { user, logout } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedTier, setSelectedTier] = useState(() => {
    // Check if tier is passed via location state
    if (location.state?.tier) {
      return location.state.tier;
    }
    return localStorage.getItem('dashboard-selected-tier') || 'Beta';
  });

  // Get user's highest accessible tier
  const getUserHighestTier = () => {
    const userTier = getUserTier();
    if (userTier === 'A+') return 'Alpha+';
    if (userTier === 'A') return 'Alpha';
    return 'Beta';
  };
  
  const [selectedTab, setSelectedTab] = useState(null);
  const [reports, setReports] = useState([]);
  const [dockStates, setDockStates] = useState({});
  const [hasUndockedWindows, setHasUndockedWindows] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [agentOpen, setAgentOpen] = useState(() => localStorage.getItem('analytics-agent-open') === '1');

  const handleNavigation = (path) => {
    navigate(path);
  };

  const getDisplayName = () => {
    return user?.fullName || user?.username || 'User';
  };

  const { isLoading, error, currentTier, serverStatus, refreshDataset, pollRefreshStatus, changeTier, checkServerHealth } = usePowerBI(selectedTier);
  const { uploadFile, isUploading } = useFileUpload();
  const [refreshStatus, setRefreshStatus] = useState('');
  
  // Normalize productTier to ensure it's always a string (handle array case from backend)
  const getUserTier = () => {
    if (!user) return 'B'; // Default if user not loaded
    const tier = user?.productTier || user?.product_tier || 'B';
    // If it's an array, get the first element; otherwise use as string
    let normalizedTier = 'B';
    if (Array.isArray(tier)) {
      normalizedTier = String(tier[0] || 'B').trim();
    } else {
      normalizedTier = String(tier || 'B').trim();
    }
    // Ensure it's one of the valid tiers
    if (!['B', 'A', 'A+'].includes(normalizedTier)) {
      normalizedTier = 'B';
    }
    return normalizedTier;
  };

  // Check if a tier is accessible based on user's product tier
  const isTierAccessible = (tier) => {
    if (!user) return false; // No access if user not loaded
    
    const userTier = getUserTier();
    const tierMap = {
      'Beta': 'B',
      'Alpha': 'A',
      'Alpha+': 'A+'
    };
    
    const requiredTier = tierMap[tier];
    if (!requiredTier) return false;

    // Tier B: Only Beta
    if (userTier === 'B') {
      return requiredTier === 'B';
    }
    
    // Tier A: Beta and Alpha
    if (userTier === 'A') {
      return requiredTier === 'B' || requiredTier === 'A';
    }
    
    // Tier A+: All tiers
    if (userTier === 'A+') {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!user) return;
    
    const userHighestTier = getUserHighestTier();
    
    // If user doesn't have access to current tier, redirect to their highest accessible tier
    if (!isTierAccessible(selectedTier)) {
      setSelectedTier(userHighestTier);
      localStorage.setItem('dashboard-selected-tier', userHighestTier);
      showInfo(`Access restricted. Redirected to ${userHighestTier} tier.`);
    } else {
      // Ensure the stored tier is valid, if not update it
      const storedTier = localStorage.getItem('dashboard-selected-tier');
      if (storedTier && !isTierAccessible(storedTier)) {
        localStorage.setItem('dashboard-selected-tier', userHighestTier);
      }
    }
  }, [user, selectedTier, showInfo]);

  // Load reports for current user
  useEffect(() => {
    const loadReports = async () => {
      try {
        if (!user) return;
        const data = await reportsService.list();
        setReports(Array.isArray(data) ? data : []);
        // Initialize selected tab if needed
        const currentTierReports = (data || []).filter(r => r.tier === selectedTier);
        const stored = localStorage.getItem(`dashboard-selected-tab-${selectedTier}`);
        const hasStored = stored && currentTierReports.some(r => r.report_type === stored);
        if (hasStored) {
          setSelectedTab(stored);
        } else if (!selectedTab && currentTierReports.length > 0) {
          setSelectedTab(currentTierReports[0].report_type);
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadReports();
  }, [user, selectedTier]);

  const activeReport = reports.find((report) => report.tier === selectedTier && report.report_type === selectedTab);
  const activeReportId = activeReport?.report_uuid || null;

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      showError('File input not available. Please refresh the page.');
      console.error('File input element not found');
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileName = file.name;
      const fileSize = (file.size / 1024 / 1024).toFixed(2);
      setSelectedFile(file);

      const message = `File selected: ${fileName} (${fileSize} MB)`;
      showInfo(message, 2000);
      
      // Ask user to confirm and load data
      setTimeout(() => {
        const confirmLoad = window.confirm(`File "${fileName}" selected. Do you want to upload it now?`);
        if (confirmLoad) {
          handleLoadData();
        }
      }, 500);
    }
  };

  const handleLoadData = async () => {
    // Use ref instead of getElementById for React compatibility
    const file = fileInputRef.current?.files[0] || selectedFile;
    if (!file) {
      showError('Please select a file first!');
      return;
    }

    try {
      const result = await uploadFile(file);
      const message = `<strong>File uploaded successfully!</strong><br/><br/>` +
        `Total records processed: ${result.statistics?.totalRowsProcessed ?? 'Unknown'}<br/>` +
        `Successfully inserted: ${result.statistics?.totalRecordsInserted ?? 'Unknown'} | Rejected: ${result.statistics?.totalRecordsFailed ?? 'Unknown'}<br/>` +
        `Success rate: ${result.statistics?.successRate ?? 'Unknown'}%`;

      showSuccess(message, 6000);




      // if (result?.rejectedRowsPerSheet && result?.originalFilePath) {
      //   const hasRejectedRows = Object.values(result.rejectedRowsPerSheet).some(rows => rows.length > 0);

      //   if (hasRejectedRows) {
      //     try {
      //       await fileService.downloadRejected(result.originalFilePath, result.rejectedRowsPerSheet);
      //       showSuccess('Rejected rows file downloaded successfully');
      //     } catch (err) {
      //       console.error('Auto-download of rejected rows failed:', err);
      //       showError('Failed to download rejected rows file.');
      //     }
      //   } else {
      //     showInfo('No rejected rows to download.');
      //   }
      // }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
    } catch (error) {
      showError(`Upload failed: ${error.message}`);
    }
  };

  const handleRefreshVisuals = async () => {
    try {
      setRefreshStatus('starting');
      showInfo('Starting PowerBI dataset refresh...');

      const refreshData = await refreshDataset();

      if (refreshData.status === 'already_in_progress') {
        setRefreshStatus('in_progress');
        showInfo('Dataset refresh is already in progress. Please wait...');

        await pollRefreshStatus(refreshData.currentRefreshId, (status) => {
          setRefreshStatus(status.status);
          showInfo(status.message || `Status: ${status.status}`);
        });
        return;
      }

      setRefreshStatus('refreshing');
      showInfo(`Dataset refresh started. Estimated duration: ${refreshData.estimatedDuration || '5-10 minutes'}`);

      await pollRefreshStatus(refreshData.refreshId, (status) => {
        setRefreshStatus(status.status);
        if (status.message) {
          showInfo(status.message);
        }
      });

      setRefreshStatus('completed');
      showSuccess('Dataset refresh completed! Updating visuals...');

      // Reload the dashboard to show updated data
      await changeTier(currentTier);
      showSuccess('Dataset and visuals refreshed successfully!');

    } catch (error) {
      setRefreshStatus('failed');
      showError(`Failed to refresh dataset: ${error.message}`);
    }
  };

  const handleTierChange = async (newTier) => {
    // Check if user has access to this tier
    if (!isTierAccessible(newTier)) {
      const defaultTier = getUserHighestTier();
      showError(`You don't have access to ${newTier} tier. Redirecting to ${defaultTier}.`);
      setSelectedTier(defaultTier);
      try { localStorage.setItem('dashboard-selected-tier', defaultTier); } catch (_) {}
      return;
    }
    
    try {
      showInfo(`Switching to ${newTier} report...`);
      await changeTier(newTier);
      setSelectedTier(newTier);
      try { localStorage.setItem('dashboard-selected-tier', newTier); } catch (_) {}
      showSuccess(`${newTier} report loaded successfully!`);
    } catch (error) {
      showError(`Failed to load ${newTier} report: ${error.message}`);
    }
  };

  useEffect(() => {
    if (location.state?.tier && location.state.tier !== selectedTier) {
      handleTierChange(location.state.tier);
    }
  }, [location.state?.tier]);

  const handleDownloadFile = async (fileId, type = 'original') => {
    try {
      showInfo(`Downloading ${type} file...`);
      await powerBiService.downloadExcel(fileId, type);
      showSuccess(`${type} file download initiated successfully!`);
    } catch (error) {
      showError(`Failed to download ${type} file: ${error.message}`);
    }
  };

  const handleRetryConnection = async () => {
    try {
      showInfo('Checking server connection...');
      await checkServerHealth();
      if (serverStatus === 'healthy') {
        showSuccess('Server connection restored! Loading dashboard...');
        await changeTier(selectedTier);
      } else {
        showError('Server is still not available. Please check if the backend server is running on port 5000.');
      }
    } catch (error) {
      showError(`Connection check failed: ${error.message}`);
    }
  };

  const getRefreshButtonText = () => {
    switch (refreshStatus) {
      case 'starting': return '🔄 Starting...';
      case 'in_progress': return '⏳ Already refreshing...';
      case 'refreshing': return '⏳ Refreshing dataset...';
      case 'completed': return '✅ Completed!';
      case 'failed': return '❌ Failed';
      default: return '🔄 Refresh visuals';
    }
  };

  const handleTabChange = (tabName) => {
    setSelectedTab(tabName);
    try { localStorage.setItem(`dashboard-selected-tab-${selectedTier}`, tabName); } catch (_) {}
  };

  const handleAddReport = async () => {
    try {
      const report_type = prompt('Enter report type name (e.g., Student):');
      if (!report_type) return;
      const report_uuid = prompt('Enter PowerBI report UUID:');
      if (!report_uuid) return;
      await reportsService.create({ tier: selectedTier, report_type, report_uuid });
      const data = await reportsService.list();
      setReports(Array.isArray(data) ? data : []);
      setSelectedTab(report_type);
      try { localStorage.setItem(`dashboard-selected-tab-${selectedTier}`, report_type); } catch (_) {}
      window.location.reload();
    } catch (e) {
      showError(e.message || 'Failed to add report');
    }
  };

  const handleDeleteReport = async (reportType) => {
    try {
      const reportToDelete = reports.find(r => r.tier === selectedTier && r.report_type === reportType);
      if (!reportToDelete) return;
      await reportsService.remove(reportToDelete.id);
      setReports(reports.filter(r => r.id !== reportToDelete.id));
      try { localStorage.setItem(`dashboard-selected-tab-${selectedTier}`, null); } catch (_) {}
      showSuccess(`Report "${reportType}" deleted successfully`);
    } catch (e) {
      showError(e.message || 'Failed to delete report');
    }
  };

  const handleDockStateChange = (tier, isDocked) => {
    setDockStates(prev => ({
      ...prev,
      [tier]: isDocked
    }));

    // Check if any windows are undocked
    const hasUndocked = Object.values({ ...dockStates, [tier]: isDocked }).some(state => !state);
    setHasUndockedWindows(hasUndocked);
  };

  const handleDockAll = () => {
    // Close all undocked windows
    Object.keys(dockStates).forEach(tier => {
      if (!dockStates[tier]) {
        const savedState = localStorage.getItem(`dock-state-${tier}`);
        if (savedState) {
          const { isDocked } = JSON.parse(savedState);
          if (!isDocked) {
            // Force dock state to true
            localStorage.setItem(`dock-state-${tier}`, JSON.stringify({
              isDocked: true,
              timestamp: Date.now()
            }));
          }
        }
      }
    });

    // Reset all dock states
    setDockStates({});
    setHasUndockedWindows(false);
    showSuccess('All reports have been docked back to the dashboard');
  };

  const analyticsTierItems = [
    { id: 'Beta', label: 'Beta', icon: 'B', disabled: !isTierAccessible('Beta'), disabledReason: 'Upgrade to access Beta' },
    { id: 'Alpha', label: 'Alpha', icon: 'A', disabled: !isTierAccessible('Alpha'), disabledReason: 'Upgrade to access Alpha' },
    { id: 'Alpha+', label: 'Alpha+', icon: 'A+', disabled: !isTierAccessible('Alpha+'), disabledReason: 'Upgrade to access Alpha+' },
  ];

  const handleAnalyticsTierSelect = (tier) => {
    handleTierChange(tier);
  };

  const toggleAgent = () => {
    setAgentOpen((open) => {
      const next = !open;
      localStorage.setItem('analytics-agent-open', next ? '1' : '0');
      return next;
    });
  };

  const renderDashboardContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingMessage}>
          <div className={styles.spinner}></div>
          <p>Loading {selectedTier} PowerBI Dashboard...</p>
          {serverStatus === 'checking' && (
            <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>Checking server connection...</p>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorMessage}>
          <div style={{ color: '#ef4444', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
            <h3 style={{ marginBottom: '1rem' }}>Dashboard Error</h3>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>{error}</p>
            {serverStatus === 'unhealthy' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                <h4 style={{ marginBottom: '0.5rem', color: '#dc2626' }}>Server Status: Offline</h4>
                <p style={{ fontSize: '0.9rem', color: '#7f1d1d' }}>
                  The backend server is not running.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={handleRetryConnection}>
                  🔄 Retry Connection
                </Button>
                <Button variant="secondary" onClick={() => handleTierChange(selectedTier)}>
                  🔄 Reload Dashboard
                </Button>
            </div>
          </div>
        </div>
      );
    }

    if (activeReportId) {
      return <PowerBIEmbed reportId={activeReportId} />;
    }

    return (
      <div className={styles.errorMessage}>
        <div style={{ color: '#ef4444', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❓</div>
          <p>No dashboard data available. Try creating a new report.</p>
          <Button variant="primary" onClick={() => handleAddReport()} style={{ marginTop: '1rem' }}>
            🔄 Create New Report
          </Button>
        </div>
      </div>
    );
  };

  return (
    <SidebarLayout
      onUploadFile={handleUploadFile}
      onLoadData={handleLoadData}
      onRefreshVisuals={handleRefreshVisuals}
      refreshStatus={refreshStatus}
      serverStatus={serverStatus}
      getRefreshButtonText={getRefreshButtonText}
      isUploading={isUploading}
      selectedTier={selectedTier}
      onTierChange={handleTierChange}
      hasSelectedFile={!!selectedFile}
      nestedNav={{
        parentId: 'analytics',
        activeId: selectedTier,
        sections: [
          {
            id: 'tiers',
            label: 'tiers',
            items: analyticsTierItems,
            onSelect: handleAnalyticsTierSelect,
          },
        ],
      }}
    >
      <div className={styles.dashboardWrapper}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          id="fileInput"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className={styles.dashboardMain}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Analytics</h2>
            <button
              type="button"
              className={`${styles.agentToggle} ${agentOpen ? styles.agentToggleOn : ''}`}
              aria-pressed={agentOpen}
              onClick={toggleAgent}
            >
              <span className={styles.agentSwitch} aria-hidden="true">
                <span className={styles.agentSwitchKnob} />
              </span>
              AI Mode
            </button>
          </div>

          <div className={`${styles.analyticsLayout} ${agentOpen ? styles.analyticsLayoutWithAgent : ''}`}>
            <div className={styles.analyticsContent}>
              <div className={styles.powerbiContainer}>
                <div className={styles.tierSelectorContainer}>
                  <EditableHeader className={styles.academicAnalyticsHeader} />
                  <DockUndockManager
                    reportId={activeReportId}
                    reportTitle="Academic Analytics"
                    tier={selectedTier}
                    onDockStateChange={(isDocked) => handleDockStateChange(selectedTier, isDocked)}
                  />
                </div>
                <MultiTab
                  tabs={reports.filter(r => r.tier === selectedTier).map(r => r.report_type)}
                  onChange={handleTabChange}
                  selectedTab={selectedTab}
                  onTabChange={handleTabChange}
                  onAdd={handleAddReport}
                  onDelete={handleDeleteReport}
                />
                <div className={`${styles.powerbiDashboard} ${!isLoading && !error ? styles.loaded : ''}`}>
                  {renderDashboardContent()}
                </div>
              </div>
            </div>
            <div
              className={`${styles.agentColumn} ${agentOpen ? styles.agentColumnOpen : ''}`}
              aria-hidden={!agentOpen}
              {...(!agentOpen ? { inert: '' } : {})}
            >
              <AcademicsAgentPanel active={agentOpen} />
            </div>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default DashboardPage;